import { LookupByNumberRequest } from './../typegen/lookup-by-number-request';
import { LookupByPhoneRequest } from './../typegen/lookup-by-phone-request';
import { PostKioskSessionDto } from './../typegen/post-kiosk-session-dto';
import { ManagerEmvTestResponse } from '../typegen/manager-emv-test-response';
import { ManagerEmvTestConfigItem } from '../typegen/manager-emv-test-config-item';
import paths from '../constants/paths';
import KioAppHelper from '../helpers/kioApp';
import logger from '../helpers/logger';
import agent from './agent';
import ApiCallType from '../types/apiCallType';
import { ClientAppHeaderNames } from '../constants/general';
import {
	LecacyPostCashBoxRemoved,
	LegacyAlertPost,
	LegacyNavUrlPost,
	LegacyKioskSessionPost,
	LegacyPostAcceptedPayment,
	LegacyPostRejectedPayment,
	LegacyResponse,
	LegacyUserEntry,
	LegacyCheckPinRequest,
} from '../types/legacyAdminApi';
import { AccountLookupResponse } from '../typegen/account-lookup-response';
import { LookupByAddressRequest } from '../typegen/lookup-by-address-request';
import { Translations } from '../types/language';
import { Log } from '../typegen/log';
import { PayWithMagTekDataRequest } from '../typegen/pay-with-mag-tek-data-request';
import { PayWithMagTekDataResponse } from '../typegen/pay-with-mag-tek-data-response';
import { PayWithCashRequest } from '../typegen/pay-with-cash-request';
import { PayWithCheckRequest } from '../typegen/pay-with-check-request';
import { PayWithCheckResponse } from '../typegen/pay-with-check-response';
import { PayWithCashResponse } from '../typegen/pay-with-cash-response';
import { PayWithTokenRequest } from '../typegen/pay-with-token-request';
import { PayWithTokenResponse } from '../typegen/pay-with-token-response';
import { store } from '../stores/store';
import { KioskConfigV1Dto } from '../typegen/kiosk-config-v1-dto';
import { PrepareTransactionRequest } from '../typegen/prepare-transaction-request';
import { PrepareTransactionResponse } from '../typegen/prepare-transaction-response';
import { toLocalDatetime } from '../helpers/datetime';
import { ApiCallServiceType } from '../types/ApiCall';
import { ulid } from 'ulid';
import sqlite from '../helpers/sqlite';
import { CashBoxTotalsDto } from '../typegen/cash-box-totals-dto';
import { CashBoxRemoval } from '../typegen/cash-box-removal';
import { ReceiptType } from '../typegen/receipt-type';
import { DigitalReceipt } from '../typegen/digital-receipt';
import { getPPBehavior } from '../integrations';

function handleLegacyResponse(response: LegacyResponse) {
	return response === 'True';
}

const api = {
	kiosks: {
		auth: async () => {
			let token = '';

			if (import.meta.env.DEV && import.meta.env.VITE_IGNORE_KW_AUTH === 'true') {
				try {
					token = btoa(`${import.meta.env.VITE_BPK_USERNAME}:${import.meta.env.VITE_BPK_PASSWORD}`);
				} catch (error) {
					logger.debug.error('There was a problem parsing the username and password values from vite', error);
				}
			} else {
				try {
					token = btoa(
						`${KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_USERNAME)}:${KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_PASSWORD)}`
					);
				} catch (error) {
					logger.debug.error('There was a problem parsing the username and password values from Kioware', error);
				}
			}

			const res = await agent.get<boolean>(paths.kiosksAuth, {
				headers: {
					Authorization: `Basic ${token}`,
				},
			});

			return res.data;
		},
		/**
		 * Posts a new kiosk session to the backend. Returns the session id from the backend.
		 */
		postSession: async (dto: PostKioskSessionDto) => {
			store.uiStore.showLoader();
			const res = await agent.post<string>(paths.kiosksPostSession, dto, {
				headers: {
					[ClientAppHeaderNames.apiCallType]: ApiCallType.KioskSession,
				},
			});
			return res.data;
		},
		isAvailable: async (): Promise<boolean> => {
			try {
				const { data } = await agent.get<boolean>(paths.isServiceAvailable);
				return data;
			} catch (error) {
				logger.logGeneral.error({}, 'Error checking service availability:', error);
				return false;
			}
		},
	},
	lookups: {
		lookupAccountNumber: async (request: LookupByNumberRequest) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<AccountLookupResponse>(paths.lookupAccountNumber, request, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.LookupRequest,
					},
				});
				return res.data;
			} catch (error) {
				await logger.logCIS.error({}, 'Error during account number lookup:', error);
			} finally {
				store.uiStore.hideLoader();
			}
		},
		lookupAddressLastName: async (request: LookupByAddressRequest) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<AccountLookupResponse>(paths.lookupAddressLastName, request, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.LookupRequest,
					},
				});
				return res.data;
			} catch (error) {
				await logger.logCIS.error({}, 'Error during address lookup:', error);
			} finally {
				store.uiStore.hideLoader();
			}
		},
		lookupPhoneLastName: async (request: LookupByPhoneRequest) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<AccountLookupResponse>(paths.lookupPhoneLastName, request, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.LookupRequest,
					},
				});
				return res.data;
			} catch (error) {
				await logger.logCIS.error({}, 'Error during phone lookup:', error);
			} finally {
				store.uiStore.hideLoader();
			}
		},
		getTransactionId: async (request: PrepareTransactionRequest) => {
			try {
				//store.uiStore.showLoader();
				const res = await agent.post<string>(paths.lookupTransactionId, request, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.LookupRequest,
					},
				});
				return res.data;
			} catch (error) {
				await logger.logCIS.error({}, 'Error during transaction ID lookup:', error);
			} finally {
				//store.uiStore.hideLoader();
			}
		},
		test: async () => {
			const res = await agent.get<boolean>(paths.lookupTest);
			return res.data;
		},
	},
	payments: {
		magtekData: async (
			paymentProviderPayment: PayWithMagTekDataRequest,
			legacyPostAcceptedPayment: LegacyPostAcceptedPayment,
			postRejectedPayment: LegacyPostRejectedPayment
		) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<PayWithMagTekDataResponse>(paths.paymentCreditCardPayments, paymentProviderPayment, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PaymentRequestCard,
					},
				});
				if (res.status === 200) {
					store.accountStore.setKioskPaymentId(res.data?.kioskPaymentId);
					legacyPostAcceptedPayment.kioskPaymentId = res.data?.kioskPaymentId;
					await sqlite.createApiCall({
						id: ulid().toUpperCase(),
						timestamp: new Date(),
						requestData: JSON.stringify(legacyPostAcceptedPayment),
						type: ApiCallServiceType.LegacyPostAcceptedPayment,
					});
				} else {
					await sqlite.createApiCall({
						id: ulid().toUpperCase(),
						timestamp: new Date(),
						requestData: JSON.stringify(postRejectedPayment),
						type: ApiCallServiceType.LegacyPostRejectedPayment,
					});
				}
				return res.data;
			} catch (error) {
				await sqlite.createApiCall({
					id: ulid().toUpperCase(),
					timestamp: new Date(),
					requestData: JSON.stringify(postRejectedPayment),
					type: ApiCallServiceType.LegacyPostRejectedPayment,
				});
				throw error;
			} finally {
				store.uiStore.hideLoader();
			}
		},
		prepareTransaction: async (request: PrepareTransactionRequest) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<PrepareTransactionResponse>(paths.prepareTransaction, request, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PrepareTransaction,
					},
				});
				if (res.status === 200) {
					return res.data;
				}
			} catch (error) {
				await logger.logPP.error({}, 'Error preparing for a transaction:', error);
			} finally {
				store.uiStore.hideLoader();
			}
		},
		cash: async (payment: PayWithCashRequest) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<PayWithCashResponse>(paths.paymentCashPayments, payment, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PaymentRequestCash,
					},
				});
				if (res.status === 200) {
					store.accountStore.setKioskPaymentId(res.data?.kioskPaymentId);
				}
				return { status: res.status, data: res.data };
			} finally {
				store.uiStore.hideLoader();
			}
		},
		check: async (
			payment: PayWithCheckRequest,
			legacyPostAcceptedPayment: LegacyPostAcceptedPayment,
			postRejectedPayment: LegacyPostRejectedPayment
		) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<PayWithCheckResponse>(paths.paymentCheckPayments, payment, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PaymentRequestCheck, // Using card type for now, may need new type
					},
				});
				if (res.status === 200) {
					store.accountStore.setKioskPaymentId(res.data?.kioskPaymentId);
					legacyPostAcceptedPayment.kioskPaymentId = res.data?.kioskPaymentId;
					legacyPostAcceptedPayment.paymentGatewayExternalID =
						getPPBehavior(store.configStore.ppType).getPaymentGatewayExternalId?.(res.data) ??
						legacyPostAcceptedPayment.paymentGatewayExternalID;
					await sqlite.createApiCall({
						id: ulid().toUpperCase(),
						timestamp: new Date(),
						requestData: JSON.stringify(legacyPostAcceptedPayment),
						type: ApiCallServiceType.LegacyPostAcceptedPayment,
					});
				} else {
					await sqlite.createApiCall({
						id: ulid().toUpperCase(),
						timestamp: new Date(),
						requestData: JSON.stringify(postRejectedPayment),
						type: ApiCallServiceType.LegacyPostRejectedPayment,
					});
				}
				return res.data;
			} catch (error) {
				await sqlite.createApiCall({
					id: ulid().toUpperCase(),
					timestamp: new Date(),
					requestData: JSON.stringify(postRejectedPayment),
					type: ApiCallServiceType.LegacyPostRejectedPayment,
				});
				throw error;
			} finally {
				store.uiStore.hideLoader();
			}
		},
		token: async (
			payment: PayWithTokenRequest,
			legacyPostAcceptedPayment: LegacyPostAcceptedPayment,
			postRejectedPayment: LegacyPostRejectedPayment
		) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post<PayWithTokenResponse>(paths.paymentTokenPayments, payment, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PaymentRequestToken,
					},
				});
				if (res.status === 200) {
					store.accountStore.setKioskPaymentId(res.data?.kioskPaymentId);
					legacyPostAcceptedPayment.kioskPaymentId = res.data?.kioskPaymentId;
					legacyPostAcceptedPayment.paymentGatewayExternalID =
						getPPBehavior(store.configStore.ppType).getPaymentGatewayExternalId?.(res.data) ??
						legacyPostAcceptedPayment.paymentGatewayExternalID;
					await sqlite.createApiCall({
						id: ulid().toUpperCase(),
						timestamp: new Date(),
						requestData: JSON.stringify(legacyPostAcceptedPayment),
						type: ApiCallServiceType.LegacyPostAcceptedPayment,
					});
				} else {
					await sqlite.createApiCall({
						id: ulid().toUpperCase(),
						timestamp: new Date(),
						requestData: JSON.stringify(postRejectedPayment),
						type: ApiCallServiceType.LegacyPostRejectedPayment,
					});
				}
				return res.data;
			} catch (error) {
				await sqlite.createApiCall({
					id: ulid().toUpperCase(),
					timestamp: new Date(),
					requestData: JSON.stringify(postRejectedPayment),
					type: ApiCallServiceType.LegacyPostRejectedPayment,
				});
				throw error;
			} finally {
				store.uiStore.hideLoader();
			}
		},
		test: async () => {
			const res = await agent.get<boolean>(paths.paymentTest);
			return res.data;
		},
		getManagerEmvTestConfigs: async () => {
			const res = await agent.get<ManagerEmvTestConfigItem[]>(paths.paymentManagerEmvTestConfigs);
			return res.data;
		},
		managerEmvTest: async (token: string, merchantConfigIndex?: number) => {
			const res = await agent.post<ManagerEmvTestResponse>(paths.paymentManagerEmvTest, { token, merchantConfigIndex });
			return res.data;
		},
	},
	receipts: {
		sendDigitalReceipt: async (receiptType: ReceiptType, receiptData: DigitalReceipt) => {
			try {
				store.uiStore.showLoader();
				const res = await agent.post(paths.getDigitalReceiptPath(receiptType), receiptData, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.DigitalReceipt,
					},
				});
				return res.data;
			} catch (error) {
				await logger.logGeneral.error({}, 'Error sending digital receipt:', error);
				throw error;
			} finally {
				store.uiStore.hideLoader();
			}
		},
	},
	configs: {
		getConfig: async (kioskId: number, version: number): Promise<KioskConfigV1Dto> => {
			const res = await agent.get(paths.getConfigPath(kioskId, version));
			return res.data;
		},
	},
	translations: {
		getTranslations: async (kioskId: number, language: string): Promise<Translations> => {
			const res = await agent.get(paths.getTranslationsPath(kioskId, language));
			return res.data;
		},
		autoTranslate: async (translations: Translations, toLang: string, fromLang: string): Promise<Translations> => {
			const res = await agent.post<Translations>(paths.getAutoTranslatePath(toLang, fromLang), translations);
			return res.data;
		},
	},
	logs: {
		uploadLog: async (log: Log): Promise<void> => {
			await agent.post<Log>(paths.appLogsUpload, log);
		},
	},
	cashBox: {
		getTotals: async (): Promise<CashBoxTotalsDto> => {
			const res = await agent.get<CashBoxTotalsDto>(paths.cashBoxTotals);
			return res.data;
		},
		remove: async (removal: CashBoxRemoval): Promise<void> => {
			await agent.post(paths.cashBoxRemove, removal);
		},
		getLastInsertTime: async (): Promise<string | null> => {
			const res = await agent.get<string | null>(paths.cashBoxLastInsertTime);
			return res.data;
		},
	},
	test: async (): Promise<boolean> => {
		return (await agent.get<boolean>(paths.test)) && (await api.legacyBpkAdmin.getIsApiActive());
	},
	legacyBpkAdmin: {
		postAlert: async (alert: LegacyAlertPost) => {
			const legacyAlert: LegacyAlertPost = { ...alert, alertDate: toLocalDatetime(alert.alertDate) };
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminPostAlert, legacyAlert, {
				headers: {
					[ClientAppHeaderNames.apiCallType]: ApiCallType.Alert,
				},
			});
			return handleLegacyResponse(res.data);
		},
		postKioskSession: async (kioskSession: LegacyKioskSessionPost) => {
			const legacyKioskSession: LegacyKioskSessionPost = {
				...kioskSession,
				startTime: toLocalDatetime(kioskSession.startTime),
				endTime: toLocalDatetime(kioskSession.endTime),
			};
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminPostKioskSession, legacyKioskSession, {
				headers: {
					[ClientAppHeaderNames.apiCallType]: ApiCallType.KioskSession,
				},
			});
			return handleLegacyResponse(res.data);
		},
		postNavSession: async (navSession: LegacyNavUrlPost) => {
			const legacyNavSession: LegacyNavUrlPost = { ...navSession, timeStamp: toLocalDatetime(navSession.timeStamp) };
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminPostNavSession, legacyNavSession, {
				headers: {
					[ClientAppHeaderNames.apiCallType]: ApiCallType.NavSession,
				},
			});
			return handleLegacyResponse(res.data);
		},
		postCashBoxRemoved: async (post: LecacyPostCashBoxRemoved) => {
			const legacyPostCashBoxRemoved: LecacyPostCashBoxRemoved = {
				...post,
				RemovalTime: toLocalDatetime(post.RemovalTime),
				insertTime: toLocalDatetime(post.insertTime),
			};
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminPostCashBoxRemoved, legacyPostCashBoxRemoved, {
				headers: {
					[ClientAppHeaderNames.apiCallType]: ApiCallType.CashBoxRemoved,
				},
			});
			return handleLegacyResponse(res.data);
		},
		getIsMaintenanceModeOn: async (post: { kioskID: number; customerID: number; passKey: string }) => {
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminGetIsMaintenanceModeOn, post);
			return handleLegacyResponse(res.data);
		},
		getIsApiActive: async () => {
			const res = await agent.get<LegacyResponse>(paths.legacyBpkAdminGetIsApiActive);
			return handleLegacyResponse(res.data);
		},
		payments: {
			postAcceptedPayment: async (payment: LegacyPostAcceptedPayment) => {
				const legacyPayment: LegacyPostAcceptedPayment = { ...payment, paymentDateTime: toLocalDatetime(payment.paymentDateTime) };
				const path =
					payment.paymentType.toLocaleLowerCase().trim() === 'cash'
						? paths.legacyBpkAdminPostAcceptedPaymentWithPaymentReference
						: paths.legacyBpkAdminPostAcceptedPayment;
				const res = await agent.post<LegacyResponse>(path, legacyPayment, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PaymentAccepted,
					},
				});
				return handleLegacyResponse(res.data);
			},
			postRejectedPayment: async (payment: LegacyPostRejectedPayment) => {
				const legacyPayment: LegacyPostRejectedPayment = { ...payment, paymentDateTime: toLocalDatetime(payment.paymentDateTime) };
				const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminPostRejectedPayment, legacyPayment, {
					headers: {
						[ClientAppHeaderNames.apiCallType]: ApiCallType.PaymentRejected,
					},
				});
				return handleLegacyResponse(res.data);
			},
		},
		/**
		 * Sends either an email or a text based on the type of string
		 */
		sendDigitalReceipt: async (receipt: { dynatouchGUID: string; email: string }) => {
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminPostDigitalReceipt, receipt, {
				headers: {
					[ClientAppHeaderNames.apiCallType]: ApiCallType.DigitalReceipt,
				},
			});
			return handleLegacyResponse(res.data);
		},
		getUserList: async (kioskInfo: { customerID: number; kioskID: number; passKey: string }) => {
			const res = await agent.post<LegacyUserEntry[]>(paths.legacyBpkAdminGetUserList, kioskInfo);
			return res.data;
		},
		checkPin: async (user: LegacyCheckPinRequest) => {
			const res = await agent.post<LegacyResponse>(paths.legacyBpkAdminCheckPin, user);
			return res.data.startsWith('True');
		},
	},
};

export default api;
